import React from 'react';
import { Link, NavLink, NavLinkProps } from 'react-router-dom';
import { useAppSelector } from 'shared/store';
import { selectIsAdmin } from 'shared/store/auth-slice';
import { Icon } from 'shared/components/Icon';
import LogoIcon from 'shared/assets/images/logo-bell-only.svg';
import { useIsGatewayActivated } from 'shared/utils/gateways';
import { HIPAAIndicator } from './HIPAAIndicator';
import { NavBarUser } from './NavBarUser';
import { OrgSelector } from './OrgSelector';
import { Gateway } from 'types';
import { NavBarAppNotifications } from './NavBarAppNotifications';
import './style.less';
import { Tooltip } from 'shared/components/Tooltip';

type NavLinkItem = NavLinkProps & {
  name: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  gateway?: Gateway;
  isUsingGateway?: boolean;
  badge?: number;
  id?: string;
};

export const NavBar: React.FC = () => {
  const hasCheckedUser = useAppSelector(s => s.auth.hasCheckedUser);
  const isAdmin = useAppSelector(selectIsAdmin);

  const links: NavLinkItem[] = [
    {
      to: '/',
      name: 'Engage',
      icon: <Icon icon="engage" className="stroke" />,
      isActive: (_, loc) =>
        loc.pathname === '/' || loc.pathname.indexOf('/visitor') === 0,
    },
    {
      to: '/spotlight',
      name: 'Spotlight',
      icon: <Icon icon="contacts" className="fill" />,
    },
    {
      to: '/scheduling',
      name: 'Scheduling',
      icon: <Icon icon="schedule" className="fill" />,
      adminOnly: false,
    },
    {
      to: '/automations',
      name: 'Automations',
      icon: <Icon icon="automations" className="stroke" />,
      adminOnly: true,
    },
    {
      to: '/ai',
      name: 'AI Chat',
      icon: <Icon icon="stars" className="stroke" />,
      adminOnly: true,
      gateway: Gateway.AI_CHAT,
      isUsingGateway: useIsGatewayActivated(Gateway.AI_CHAT),
    },
    {
      to: '/analytics',
      name: 'Analytics',
      icon: <Icon icon="analytics" className="fill" />,
      adminOnly: true,
    },
    {
      to: '/help-center',
      name: 'Help Center',
      icon: <Icon icon="helpCenter" className="fill" />,
      gateway: Gateway.HELP_CENTER,
      isUsingGateway: useIsGatewayActivated(Gateway.HELP_CENTER),
      adminOnly: true,
    },
    {
      to: '/outbound',
      name: 'Dialer',
      icon: <Icon icon="dialer" className="stroke" />,
      gateway: Gateway.OUTBOUND,
      isUsingGateway: useIsGatewayActivated(Gateway.OUTBOUND),
    },
    {
      to: '/salesfloor',
      name: 'Salesfloor',
      icon: <Icon icon="group" className="stroke" />,
      gateway: Gateway.SALESFLOOR,
      isUsingGateway: useIsGatewayActivated(Gateway.SALESFLOOR),
      id: 'salesfloor',
    },
    {
      to: '/settings',
      name: 'Settings',
      icon: <Icon icon="settings" className="stroke" />,
    },
  ];

  const linksToRender = links.filter(l => {
    if (!hasCheckedUser) {
      return false;
    }
    if (l.adminOnly && !isAdmin) {
      return false;
    }
    if (l.gateway && !l.isUsingGateway) {
      return false;
    }
    return true;
  });

  return (
    <div className="NavBar">
      <Link className="NavBar__logo" to="/">
        <LogoIcon />
      </Link>
      <div className="NavBar__main">
        {linksToRender.map((link, key) => (
          <Tooltip
            overlayClassName="NavBar__tooltip"
            theme="light"
            key={link.name}
            title={link.name}
            placement="right"
            mouseEnterDelay={0.1}
          >
            <NavLink
              id={link.id ? `nav-link-${link.id}` : undefined}
              style={{ '--i': key } as React.CSSProperties}
              to={link.to}
              isActive={link.isActive}
              className="NavBar__link  NavBar__main-item"
            >
              <div className="NavBar__link-item">
                {link.icon}
                {!!link.badge && <span>{link.badge > 99 ? '99+' : link.badge}</span>}
              </div>
            </NavLink>
          </Tooltip>
        ))}
      </div>
      <div className="NavBar__end">
        <div className="NavBar__end-item">
          <NavBarAppNotifications />
        </div>
        <div className="NavBar__end-item">
          <HIPAAIndicator />
        </div>
        {isAdmin && (
          <div className="NavBar__end-item NavBar__optional">
            <Tooltip
              theme="light"
              title={'Invite members'}
              placement="right"
              overlayClassName="NavBar__tooltip"
              mouseEnterDelay={0.1}
            >
              <Link
                className="NavBar__link  NavBar__main-item NavBar__end-invite"
                to="/settings/members"
              >
                <div className="NavBar__link-item">
                  <Icon icon="invite" className="stroke" />
                </div>
              </Link>
            </Tooltip>
          </div>
        )}
        <div className="NavBar__end-item">
          <OrgSelector />
        </div>
        <div className="NavBar__end-item">
          <NavBarUser />
        </div>
      </div>
    </div>
  );
};
